// Cookie consent + analytics loader — STRICT MODE
// No analytics scripts (GA4, PostHog) run at all until the user gives consent.
// If consent already exists in localStorage, we hydrate the trackers on page load.

const STORAGE_KEY = 'cookie_consent_v1';
const CHANGE_EVENT = 'cookie_consent_changed';
const GA_MEASUREMENT_ID = 'G-5ZWEQD4XBE';
const POSTHOG_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let gaLoaded = false;
let posthogLoaded = false;

export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const hasDecision = () => getConsent() !== null;

export const analyticsAllowed = () => {
  const c = getConsent();
  return !!(c && c.analytics);
};

// ---------- Dynamic Loaders (only invoked with explicit consent) ----------

const loadGA = () => {
  if (gaLoaded) return;
  if (typeof window === 'undefined') return;

  // Set up dataLayer/gtag stub
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });

  // Inject the gtag script
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);

  gaLoaded = true;
};

const loadPostHog = () => {
  if (posthogLoaded) return;
  if (typeof window === 'undefined') return;

  // PostHog official snippet (stub + async script)
  /* eslint-disable */
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
  /* eslint-enable */

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    disable_session_recording: true,
  });

  posthogLoaded = true;
};

// Load whichever trackers are consented (called on page load + on consent change)
export const applyConsent = () => {
  const c = getConsent();
  if (c && c.analytics) {
    loadGA();
    loadPostHog();
  }
};

// ---------- Save / Change / Subscribe ----------

export const saveConsent = ({ analytics }) => {
  const payload = {
    necessary: true,
    analytics: !!analytics,
    timestamp: new Date().toISOString(),
    version: 1,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));

  // Inject trackers immediately if the user opted in
  if (analytics) {
    loadGA();
    loadPostHog();
  } else {
    // If they had previously accepted and now revoke, opt PostHog out defensively
    if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      try { window.posthog.opt_out_capturing(); } catch (_e) { /* noop */ }
    }
  }

  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: payload }));
  return payload;
};

export const onConsentChange = (handler) => {
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
};

export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent('open_cookie_preferences'));
};
